// Redesign shell: menu dialog, header dropdowns (SHOP, a link with sub-links), bag count, screen-reader announcer, FAQ deep links and product tile swatches.
// Listeners sit on the document and look elements up as they fire, so a header the theme editor re-renders still works.
const navMenus = () => document.querySelectorAll('.rd .desktop-nav > details');
// The menu photo is lazy (sections/stally-rd-header.liquid); eager starts its download even inside the closed menu.
const loadMenuPhoto = () => {
  const photo = document.getElementById('menu-photo');
  if (photo) photo.loading = 'eager';
};

// Shopper-usage counts (S-051, Jasen, Oct 26): five named GA4 events, sent through the Google & YouTube app's gtag only
// while the shopper allows analytics, never from the theme editor. Other redesign scripts dispatch 'stally:track' with
// the name.
const EVENTS = ['stally_xsell_click', 'stally_size_guide_open', 'stally_swatch_tap', 'stally_quick_open', 'stally_quick_add'];
// any script can dispatch 'stally:track': one of our event names only
const trackUse = name => {
  try {
    if (EVENTS.includes(name) && !window.Shopify?.designMode && window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()) window.gtag?.('event', name, { send_to: 'G-79LL08DMK2' });
  } catch {} // a missing or failing tag never breaks the tap that sent it
};
document.addEventListener('stally:track', event => trackUse(event.detail));
// A tap that leaves the page ('stally:track-next': a cross-sell tile, a hat's color link, the size guide link of a
// product with no chart) is sent by the next page, with that page's address: GA4 holds events about 5 s and drops them
// when the page unloads (measured Oct 26).
const PENDING = 'stally:track-pending';
let pending = null;
try {
  const stored = sessionStorage.getItem(PENDING);
  sessionStorage.removeItem(PENDING);
  pending = stored; // only once removed: a name that can't be removed would be sent by every later page
} catch {}
// anyone can write sessionStorage: one of our event names only
if (!EVENTS.includes(pending)) pending = null;
// a page leaves once, so its last leaving tap (a re-tap, a double-click, a second link) is the one kept
document.addEventListener('stally:track-next', event => {
  // nothing is stored without analytics consent, nor from the theme editor, and a foreign name never replaces ours
  if (!EVENTS.includes(event.detail)) return;
  // shortcut: a leaving tap before the consent API loads is dropped too. It loads about 0.2 s after the page's scripts
  // run on desktop, up to about 1.4 s on a throttled phone (Oct 28, the scripts ahead of Shopify's). If those taps
  // matter, store unless analyticsProcessingAllowed() returns false (that would store before consent is known); taps
  // before the scripts run aren't seen either way.
  if (window.Shopify?.designMode || !window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()) return;
  try { sessionStorage.setItem(PENDING, event.detail); } catch {}
});
// sent once the consent API has loaded; the Google & YouTube app's gtag can be later still (about 40 ms on desktop, up
// to 3 s on a throttled phone, cold cache, Oct 28), well inside the 10 s of retries below
// shortcut: leaving a page within about 5 s of any send (here or a live tap) loses that tap (GA4's batching, as for its
// own page_view); revisit if GA4 gets a way to send at once
const sendPending = (tries = 40) => {
  if (!pending) return;
  if (window.gtag) {
    trackUse(pending);
    pending = null;
  } else if (tries) setTimeout(sendPending, 250, tries - 1);
};
window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.2' }], () => sendPending());

document.addEventListener('click', event => {
  if (!(event.target instanceof Element)) return;
  const opener = event.target.closest('[data-open]');
  if (opener) {
    const dialog = document.getElementById(opener.dataset.open);
    if (dialog?.id === 'menu-dialog') loadMenuPhoto();
    for (const nav of navMenus()) {
      if (!nav.open) continue;
      nav.querySelector('summary').focus();
      nav.open = false;
    }
    document.querySelectorAll('.rd dialog[open]').forEach(open => { if (open !== dialog) open.close(); });
    if (dialog && !dialog.open) dialog.showModal();
  }
  event.target.closest('[data-close]')?.closest('dialog')?.close();
  if (event.target.closest('#menu-dialog a')) document.getElementById('menu-dialog').close();
});

// Product tile swatches (snippets/stally-rd-card.liquid): a color shows its photo and points the tile at that color;
// a hat's color is its own product, so its name and price come with it.
document.addEventListener('click', event => {
  // buttons only: a tile's single color is a plain span.swatch with no link to switch to
  const swatch = event.target instanceof Element ? event.target.closest('.pg-tile button.swatch') : null;
  if (!swatch?.dataset.href) return;
  const tile = swatch.closest('.pg-tile');
  const link = tile.querySelector('.pg-card');
  const photo = link.querySelector('.pg-card-media img');
  if (photo && swatch.dataset.src) Object.assign(photo, { srcset: swatch.dataset.srcset, src: swatch.dataset.src, alt: swatch.dataset.alt });
  // the model's size ⓘ follows the photo (the swatch's press already closed an open note)
  const tip = tile.querySelector('.rd-tip');
  if (tip && swatch.dataset.src) {
    tip.hidden = !swatch.dataset.model;
    tip.querySelector('.rd-tip-text').textContent = swatch.dataset.model || '';
  }
  link.href = swatch.dataset.href;
  if (swatch.dataset.title) {
    link.querySelector('.pg-card-meta strong').textContent = swatch.dataset.title;
    link.querySelector('.pg-card-meta > span').textContent = swatch.dataset.price;
  }
  tile.querySelectorAll('.swatch').forEach(button => button.setAttribute('aria-pressed', String(button === swatch)));
  // a sold-out color or hat shows Sold out over the photo
  tile.querySelector('.pg-card-sold')?.toggleAttribute('hidden', !('soldout' in swatch.dataset));
  trackUse('stally_swatch_tap');
});

// The model's size ⓘ over product photos (snippets/stally-rd-model-info.liquid; the product page's lightbox, the
// homepage cards and quick shop draw it too) and the review count's ⓘ (snippets/stally-rd-affiliate-info.liquid): it
// opens on mouse hover or keyboard focus, a click or tap pins it, and it closes on Escape, a press anywhere but its
// button, leaving it with the mouse, or Tab away. Delegated, so ⓘs drawn later work. One note is open at a time.
const TIP = '.rd-tip, .rv-info', TIP_BUTTON = '.rd-tip-btn, .rv-info-btn', TIP_NOTE = '.rd-tip-text, .rv-info-tip';
const tipStates = new WeakMap();
let openTip = null;
let tipLeaving = 0;
function setTip(tip, change) {
  const state = { ...tipStates.get(tip), ...change };
  tipStates.set(tip, state);
  const open = Boolean(state.hovered || state.focused || state.pinned);
  if (open && openTip !== tip) {
    if (openTip) setTip(openTip, { hovered: false, focused: false, pinned: false });
    openTip = tip;
  } else if (!open && openTip === tip) openTip = null;
  const note = tip.querySelector(TIP_NOTE);
  note.hidden = !open;
  note.style.translate = '';
  note.style.removeProperty('--tip-shift');
  if (!open) return;
  // kept 12px inside the window (a tile at a phone's left edge) and the review count's inside the reviews' column (it
  // sits at the right on wide screens), its caret still under the icon
  const box = note.getBoundingClientRect(), column = tip.closest('.rv-head')?.getBoundingClientRect();
  const left = Math.max(12, column?.left ?? 12), right = Math.min(document.documentElement.clientWidth - 12, column?.right ?? Infinity);
  const shift = box.left < left ? left - box.left : box.right > right ? right - box.right : 0;
  if (shift) {
    note.style.translate = `${shift}px 0`;
    note.style.setProperty('--tip-shift', `${shift}px`);
  }
}
const closeTip = () => { if (openTip) setTip(openTip, { hovered: false, focused: false, pinned: false }); };
const tipAt = target => (target instanceof Element ? target.closest(TIP) : null);
// mouse only: a tap is handled by its click, and a touch would leave the hover set. The delay lets the pointer cross
// from the icon onto the note.
// A move between the icon's parts (its svg's circle and paths) is not an arrival: after Escape, the note stays closed
// until the pointer leaves and comes back.
document.addEventListener('pointerover', event => {
  const tip = tipAt(event.target);
  if (!tip || event.pointerType !== 'mouse' || tip.contains(event.relatedTarget)) return;
  clearTimeout(tipLeaving);
  if (!tipStates.get(tip)?.hovered) setTip(tip, { hovered: true });
});
document.addEventListener('pointerout', event => {
  const tip = tipAt(event.target);
  if (!tip || event.pointerType !== 'mouse' || tip.contains(event.relatedTarget)) return;
  tipLeaving = setTimeout(() => setTip(tip, { hovered: false }), 200);
});
document.addEventListener('focusin', event => {
  if (event.target instanceof Element && event.target.matches(TIP_BUTTON)) setTip(tipAt(event.target), { focused: event.target.matches(':focus-visible') });
});
document.addEventListener('focusout', event => {
  if (event.target instanceof Element && event.target.matches(TIP_BUTTON)) setTip(tipAt(event.target), { focused: false, pinned: false });
});
document.addEventListener('click', event => {
  const button = event.target instanceof Element ? event.target.closest(TIP_BUTTON) : null;
  if (button) setTip(tipAt(button), { pinned: !tipStates.get(tipAt(button))?.pinned });
});
// pointerdown, not click: iOS Safari sends the document no click for a tap on plain text
document.addEventListener('pointerdown', event => {
  if (openTip && !openTip.querySelector(TIP_BUTTON).contains(event.target)) closeTip();
});
// Escape closes an open note, and only the note: a quick shop or lightbox dialog under it stays open
document.addEventListener('keydown', event => {
  if (event.key !== 'Escape' || !openTip) return;
  // a note redrawn away (a homepage card's color, a theme editor reload) leaves the Escape to the page
  if (!openTip.isConnected) {
    openTip = null;
    return;
  }
  event.preventDefault();
  closeTip();
});

// Where the menu button shows, the photo loads once the page has, ahead of a first open.
addEventListener('load', () => {
  if (!document.querySelector('.rd .menu-trigger')?.getClientRects().length) return;
  (window.requestIdleCallback || setTimeout)(loadMenuPhoto);
}, { once: true });

const closeOutside = event => {
  for (const nav of navMenus()) if (nav.open && !nav.contains(event.target)) nav.open = false;
};
document.addEventListener('pointerdown', closeOutside);
document.addEventListener('focusin', closeOutside);
document.addEventListener('keydown', event => {
  const nav = event.target instanceof Element ? event.target.closest('.rd .desktop-nav > details') : null;
  if (event.key === 'Escape' && nav?.open) {
    event.preventDefault();
    nav.open = false;
    nav.querySelector('summary').focus();
  }
});
// The desktop nav hides at tablet widths; a dropdown left open there would float with no summary.
function watchHeader() {
  const header = document.querySelector('.rd .shop-header');
  if (!header) return;
  new ResizeObserver(() => {
    for (const nav of navMenus()) if (!nav.querySelector('summary').getClientRects().length) nav.open = false;
  }).observe(header);
}
watchHeader();
document.addEventListener('shopify:section:load', event => {
  if (event.target.querySelector('.shop-header')) watchHeader();
});

// Safari has no <dialog closedby>: a click on the backdrop of any redesign dialog closes it.
if (!('closedBy' in HTMLDialogElement.prototype)) {
  document.addEventListener('click', event => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.matches('.rd dialog[closedby="any"]')) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
  });
}

// Bag count sources: redesign scripts' 'stally:cart' (detail = the Ajax API cart) and Concept's theme.js pubsub events.
function setBagCount(count) {
  const badge = document.getElementById('bag-count');
  if (!badge || !Number.isInteger(count)) return;
  badge.textContent = count;
  badge.closest('a')?.setAttribute('aria-label', `Bag, ${count} ${count === 1 ? 'item' : 'items'}`);
}
document.addEventListener('stally:cart', event => setBagCount(event.detail?.item_count));
if (window.theme?.pubsub) {
  theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, event => {
    if (!event.cart?.errors) setBagCount(event.cart?.item_count);
  });
}

// One status region outside every section: a live region replaced along with its section isn't announced.
const announcer = document.createElement('p');
announcer.id = 'rd-announcer';
announcer.setAttribute('role', 'status');
announcer.style.cssText = 'position:absolute;width:1px;height:1px;margin:-1px;overflow:hidden;clip-path:inset(50%);white-space:nowrap';
document.body.append(announcer); // created at load: a region added with its first message is often not read
document.addEventListener('stally:announce', event => {
  const region = announcer;
  region.textContent = '';
  requestAnimationFrame(() => { region.textContent = String(event.detail || ''); });
});

// FAQ: a link to #<question> opens that question (ids are handleized headings, so no decoding).
const openHash = () => {
  const target = location.hash ? document.getElementById(location.hash.slice(1)) : null;
  if (target?.matches('.rd details')) target.open = true;
};
window.addEventListener('hashchange', openHash);
openHash();

// Shopify's cookie banner, and its preferences panel, come before the Shopify Forms popup (the 15% sign-up app embed):
// while one holds it, data-consent-open on <html> hides the popup (sections/stally-rd-header.liquid's stylesheet). The
// banner holds it until the visitor answers, on a tab's first page only: from the second (a reload counts), the popup
// shows over it (Jasen, Oct 27). The preferences panel, opened on purpose, holds it on any page. Each panel's size is
// measured rather than Shopify's markup read, so a panel Shopify removes or sets to display: none lets the popup through.
// shortcut: Escape in a cookie panel also reaches the popup's own Escape listener (on document), which dismisses or
// minimizes it unseen, as it did seen before; stopping the key at the panel would also hide it from Shopify's one-time
// Tab listener on window. Revisit if the popup's sign-ups drop once this ships.
const consentPanels = ['shopify-pc__banner', 'shopify-pc__prefs']; // watched: Shopify shows and hides these
const consentWatched = new WeakSet();
let consentWasOpen = false;
// without sessionStorage every page counts as a first one
let consentFirstPage = true;
try {
  consentFirstPage = !sessionStorage.getItem('stally:consent-page-seen');
  sessionStorage.setItem('stally:consent-page-seen', '1');
} catch {}
const consentShown = id => {
  const box = document.getElementById(id)?.getBoundingClientRect();
  return box?.width > 0 && box.height > 0;
};
const syncConsentOpen = () => {
  // the prefs wrapper has no height of its own, so its dialog is measured
  const open = (consentFirstPage && consentShown('shopify-pc__banner')) || consentShown('shopify-pc__prefs__dialog');
  document.documentElement.toggleAttribute('data-consent-open', open);
  // the popup focuses its first field once, as it opens; one that opened while hidden gets focus on its dialog as it
  // shows (its name is read, and a phone's keyboard doesn't pop up), a task later so an Escape that also closes the
  // popup lands first
  if (consentWasOpen && !open) setTimeout(() => document.querySelector('shopify-forms-embed[id^="app-embed-container-"]')?.shadowRoot?.querySelector('[role="dialog"][tabindex="-1"]')?.focus());
  consentWasOpen = open;
};
const watchConsent = () => {
  for (const id of consentPanels) {
    const panel = document.getElementById(id);
    if (panel && !consentWatched.has(panel)) {
      consentWatched.add(panel);
      new MutationObserver(syncConsentOpen).observe(panel, { attributes: true, subtree: true });
    }
  }
  syncConsentOpen();
};
new MutationObserver(watchConsent).observe(document.body, { childList: true, attributes: true });
watchConsent();

// Shopify's sign-up popup and delivery estimate turn autofill off on their email and ZIP fields, and the popup's dialog
// has no name (WCAG 1.3.5, 4.1.2). Both draw late inside their own shadow roots, so each root is watched; on a phone the
// estimate's ZIP form opens in a root of its own, on a plain div on the body.
// shortcut: these ids and attributes are Shopify's, not a promised API; if they change the fields just go back to as sold.
const fixShopifyParts = root => {
  root.querySelector('input#email[autocomplete="off"]')?.setAttribute('autocomplete', 'email');
  root.querySelector('input#postal-code__input[autocomplete="off"]')?.setAttribute('autocomplete', 'postal-code');
  // the phone ZIP drawer's backdrop is a button wrapped round the form; its own close button stays, and a tap outside still closes
  root.querySelectorAll('.MobileDrawerV2__Overlay[role="button"]').forEach(overlay => ['role', 'tabindex', 'aria-label'].forEach(name => overlay.removeAttribute(name)));
  root.querySelectorAll('[role="dialog"]:not([aria-label], [aria-labelledby])').forEach(dialog => {
    const title = dialog.querySelector('h1, h2, h3')?.textContent.trim();
    if (title) dialog.setAttribute('aria-label', title);
  });
};
const shopifyRoots = new WeakSet();
let shopifyScan = 0;
const scanShopifyParts = () => {
  shopifyScan = 0;
  document.querySelectorAll('shopify-forms-embed, delivery-promise-wc, body > div').forEach(({ shadowRoot: root }) => {
    if (!root || shopifyRoots.has(root)) return;
    shopifyRoots.add(root);
    fixShopifyParts(root);
    new MutationObserver(() => fixShopifyParts(root)).observe(root, { childList: true, subtree: true });
  });
};
new MutationObserver(() => { if (!shopifyScan) shopifyScan = requestAnimationFrame(scanShopifyParts); }).observe(document.body, { childList: true, subtree: true });
scanShopifyParts();
